import type { ReactNode } from 'react'
import { UsersRound, User, Link2, Share2, Lock } from 'lucide-react'
import { Tooltip, avatarBg, getInitials, nameFromEmail } from '@papan/core-ui/ui'
import type { ProjectAccessSummary } from './home/data'

/**
 * Shared Access-summary presentation — the vocabulary used by the drive Access
 * column (`DriveView` `AccessCell`) AND the Projects / MiniApps card access line.
 * Extracted so both render the same faces, badges and tooltip breakdown from one
 * source (docs/plans/2026-07-14_(completed)_PROJECTS_MINIAPPS_VIEW_REVAMP.md).
 */

type Translate = (key: string, options?: Record<string, unknown>) => string

export interface AccessTooltipRow { icon: ReactNode; text: string }

export function AccessTooltip({ rows }: { rows: AccessTooltipRow[] }) {
  return (
    <div className="text-left leading-snug space-y-1">
      {rows.map((r, i) => (
        <div key={i} className="flex items-center gap-1.5 whitespace-nowrap">
          <span className="shrink-0">{r.icon}</span>
          <span>{r.text}</span>
        </div>
      ))}
    </div>
  )
}

/** "Dev, Admin and 2 more teams" / "Alice, Bob and 3 more" — up to two names,
 *  then a remainder count. */
export function nameBreakdown(
  names: string[],
  total: number,
  kind: 'teams' | 'people',
  t: Translate,
): string {
  const shown = names.slice(0, 2)
  const base = shown.join(', ')
  const remaining = Math.max(0, total - shown.length)
  if (remaining <= 0) return base
  return t(kind === 'teams' ? 'driveView.access.andMoreTeams' : 'driveView.access.andMore', { names: base, count: remaining })
}

/**
 * One avatar in the Access facepile — same palette + hash as the Who-has-access /
 * share-links dialogs. The OWNER avatar carries the accent ring
 * (`.ws-access-avatar-owner`), an identity marker unique to the owner.
 */
export function AccessAvatar({ seed, label, owner, overlap, sm }: { seed: string; label: string; owner?: boolean; overlap?: boolean; sm?: boolean }) {
  return (
    <span
      aria-hidden
      className={`${sm ? 'w-4 h-4 text-[8px]' : 'w-5 h-5 text-[9px]'} rounded-full inline-flex items-center justify-center font-semibold text-white shrink-0 ${avatarBg(seed)} ${owner ? 'ws-access-avatar-owner' : 'ws-access-avatar'} ${overlap ? '-ml-1' : ''}`}
    >
      {getInitials(label)}
    </span>
  )
}

/**
 * The card access line — the always-present companion to the drive Access
 * column, laid out for a project/mini-app card (no responsive tiering; the line
 * ALWAYS reserves its height so cards stay aligned even with nothing to show).
 * Works on personal (links/outsiders, "Only you") and org (floor / teams /
 * people / links / outsiders) tenants.
 */
export function ProjectAccessLine({ access, owner, creator, memberNames, t }: {
  access?: ProjectAccessSummary
  /** Promotes the owner to the lead badge (with the accent ring). Distinct from
   *  `creator` below, and currently passed by no call site. */
  owner?: { name: string; email?: string }
  /** The card's creator, for NAME RESOLUTION ONLY — never lead selection.
   *  `access.people` carries emails, so a grantee who is also the creator would
   *  render email-derived initials ("SE" from seasoncafe@…) directly beneath
   *  the creator badge's name-derived ones ("SC" from "Season Chu"), making one
   *  account look like two people. Passing it through `owner` instead would
   *  swap the grantee badge for the owner's AND then count that same person
   *  again in the "+N" remainder — so it is deliberately a separate input. */
  creator?: { name: string; email?: string }
  /** Real display names for org members, from `useOrgMemberNames`. Optional so
   *  a surface that has not adopted it keeps the derived name, and so the line
   *  renders correctly before the query resolves. */
  memberNames?: Map<string, string>
  t: Translate
}) {
  const floor = access?.floor
  const locked = !!access?.locked
  const links = access?.links ?? 0
  const outsiders = access?.outsiders ?? 0
  const teams = access?.teams ?? []
  const teamCount = access?.teamCount ?? 0
  const people = access?.people ?? []
  const peopleCount = access?.peopleCount ?? 0

  // A grantee email resolves to a real display name — the org member list
  // first, then the creator (whose name rides along on the card and is
  // available before the query settles), and only then the email's local-part.
  //
  // That last fallback is why this exists: `nameFromEmail` can only capitalise
  // the local-part, so it renders "Kok2" for an account called "Kok 2". With
  // the creator resolved and nobody else, one tooltip read "Kok Kok, Kok2" —
  // two naming rules side by side in the same line.
  //
  // Case-folded on both sides: a grant's `target_id` case cannot be assumed
  // (see the note on `avatarBg`).
  const displayNameFor = (email: string) => {
    const key = email.trim().toLowerCase()
    const known = memberNames?.get(key)
    if (known) return known
    if (creator?.email && creator.email.trim().toLowerCase() === key) return creator.name
    return nameFromEmail(email)
  }

  // Tooltip = a plain who-has-access breakdown (icon-prefixed lines).
  const tipRows: AccessTooltipRow[] = []
  if (teamCount > 0) tipRows.push({ icon: <UsersRound size={12} className="text-blue-300" />, text: nameBreakdown(teams.map(x => x.name), teamCount, 'teams', t) })
  if (peopleCount > 0) tipRows.push({ icon: <User size={12} className="text-zinc-300" />, text: nameBreakdown(people.map(displayNameFor), peopleCount, 'people', t) })
  if (links > 0) tipRows.push({ icon: <Link2 size={12} className="text-sky-300" />, text: t('driveView.access.links', { count: links }) })
  if (outsiders > 0) tipRows.push({ icon: <Share2 size={12} className="text-purple-300" />, text: t('driveView.access.outsiders', { count: outsiders }) })

  // Compact: ONE lead badge (most-important access indicator) + a "+N" remainder.
  const total = teamCount + peopleCount + links + outsiders
  let lead: ReactNode = null
  let covered = 0 // grantees the lead badge itself represents
  if (teamCount > 0) {
    lead = (
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full ws-team-badge min-w-0 shrink">
        <UsersRound size={10} className="shrink-0" />
        <span className="truncate max-w-[7rem]">{teams[0].name}</span>
      </span>
    )
    covered = 1
  } else if (owner) {
    lead = <AccessAvatar seed={owner.email || owner.name} label={owner.name} owner sm />
  } else if (peopleCount > 0 && people.length > 0) {
    lead = <AccessAvatar seed={people[0]} label={displayNameFor(people[0])} sm />
    covered = 1
  } else if (links > 0) {
    lead = <span className="ws-access-link inline-flex items-center gap-0.5 shrink-0"><Link2 size={11} />{links}</span>
    covered = links
  } else if (outsiders > 0) {
    lead = <span className="ws-access-outsider inline-flex items-center gap-0.5 shrink-0"><Share2 size={11} />{outsiders}</span>
    covered = outsiders
  }
  const remaining = Math.max(0, total - covered)

  const inner = (
    <>
      {locked && <Lock size={11} className="themed-text-muted shrink-0" />}
      {!lead && floor === 'member' && <span className="font-medium">{t('driveView.access.memberPlus')}</span>}
      {lead}
      {remaining > 0 && (
        <span className="text-[9px] font-medium px-1 py-0.5 rounded-full themed-bg-tertiary themed-text-muted">+{remaining}</span>
      )}
    </>
  )

  const line = <span className="inline-flex items-center gap-1 min-h-[18px] text-[11px] themed-text-muted max-w-full overflow-hidden">{inner}</span>

  return tipRows.length > 0
    ? <Tooltip content={<AccessTooltip rows={tipRows} />} always className="inline-flex max-w-full opacity-70 saturate-50">{line}</Tooltip>
    : line
}
